import { Box, Text } from '@factorialco/deprecated-design-system'
import type {
  SoftwareManagementApplicationsListPageQueryQuery,
  SoftwareManagementSoftwareInfoSoftwareSetupFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DataIndicator from 'modules/finance/components/DataIndicator'
import CardsBanner from 'modules/software_management/components/CardsBanner'
import * as styles from 'modules/software_management/components/SoftwareDashboard/index.vanilla.css'
import { dashboardCostForMonth } from 'modules/software_management/lib/costs'
import { currentMonth, previousMonth } from 'modules/software_management/lib/dates'
import { useApplicationsContext } from 'modules/software_management/pages/ApplicationsPage'

type SoftwareManagementDashboardSoftwareCost = NonNullable<
  NonNullable<
    SoftwareManagementApplicationsListPageQueryQuery['softwareManagement']
  >['dashboardSoftwareCostsConnection']
>['nodes'][number]

interface Props {
  softwareSetups: SoftwareManagementSoftwareInfoSoftwareSetupFragment[]
  dashboardCosts: SoftwareManagementDashboardSoftwareCost[]
  currency: string
}

const Header = ({ softwareSetups, dashboardCosts, currency }: Props) => {
  const { accountProvidersAvailable, bankAccount, onboardingInProgress } = useApplicationsContext()
  const { availableBalanceCents, currency: balanceCurrency } = bankAccount || {}
  const {
    access: { locale },
  } = useCurrentSession()

  const currentSpend = dashboardCostForMonth(dashboardCosts, currentMonth())
  const previousSpend = dashboardCostForMonth(dashboardCosts, previousMonth())

  const spendDifference = currentSpend - previousSpend

  const BalanceInfo = () => {
    return (
      <>
        <Box>
          <Text size="200">
            {i18n.t('software_management.software_setups.dashboard.header.expected_spend')}
          </Text>
          <Text size="500" weight="medium" color="grey1000">
            {MoneyWithCurrency({ amount: previousSpend, currency, locale })}
          </Text>
        </Box>
        {availableBalanceCents !== undefined && balanceCurrency && (
          <Box>
            <Text size="200">{i18n.t('expenses_v2.current_balance')}</Text>
            <Text size="500" weight="medium" color="grey1000">
              {MoneyWithCurrency({
                amount: availableBalanceCents,
                currency: balanceCurrency,
                locale,
              })}
            </Text>
          </Box>
        )}
      </>
    )
  }
  return (
    <Box flexDirection="row" gap="s24">
      <Box
        className={styles.header}
        flexDirection="row"
        alignItems="center"
        paddingY="s12"
        paddingLeft="s24"
        paddingRight={!!bankAccount || !accountProvidersAvailable ? 's96' : 's12'}
        justifyContent={!!bankAccount || !accountProvidersAvailable ? 'spaceBetween' : 'flexStart'}
        width="full"
      >
        <Box>
          <Text size="200">
            {i18n.t('software_management.software_setups.dashboard.header.apps')}
          </Text>
          <Text size="500" weight="medium" color="grey1000">
            {softwareSetups.length}
          </Text>
        </Box>
        <Box>
          <Text size="200">
            {i18n.t('software_management.software_setups.dashboard.header.current_spend')}
          </Text>
          <Box flexDirection="row" alignItems="center" gap="s16">
            <Text size="500" weight="medium" color="grey1000">
              {MoneyWithCurrency({ amount: currentSpend, currency, locale })}
            </Text>
            {currentSpend > 0 && spendDifference !== 0 && (
              <Box flexDirection="row" gap="s2" alignItems="center">
                <DataIndicator change={spendDifference} />

                <Box gap="s4" flexDirection="row">
                  <Text weight="semibold" size="200">
                    {MoneyWithCurrency({ amount: Math.abs(spendDifference), currency, locale })}
                  </Text>
                  <Text weight="normal" size="200">
                    {i18n.t('insights.widgets.shared.from_previous_months')}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        {!!bankAccount && <BalanceInfo />}
      </Box>
      {accountProvidersAvailable && !bankAccount && (
        <CardsBanner showCompleteAccount={onboardingInProgress} />
      )}
    </Box>
  )
}

export default Header
